import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getFavoriteIds, addToFavorites, removeFromFavorites } from '../lib/api';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const FavoritesContext = createContext(null);

export const FavoritesProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadFavorites = useCallback(async () => {
    if (!isAuthenticated) {
      setFavoriteIds([]);
      return;
    }
    try {
      const res = await getFavoriteIds();
      setFavoriteIds(res.data.favorite_ids || []);
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  const isFavorite = (listingId) => favoriteIds.includes(listingId);

  const toggleFavorite = async (listingId) => {
    if (!isAuthenticated) {
      toast.error('Connectez-vous pour sauvegarder vos favoris');
      return;
    }

    setLoading(true);
    try {
      if (isFavorite(listingId)) {
        await removeFromFavorites(listingId);
        setFavoriteIds(prev => prev.filter(id => id !== listingId));
        toast.success('Retiré des favoris');
      } else {
        await addToFavorites(listingId);
        setFavoriteIds(prev => [...prev, listingId]);
        toast.success('Ajouté aux favoris');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <FavoritesContext.Provider value={{ favoriteIds, isFavorite, toggleFavorite, loading, refreshFavorites: loadFavorites }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};
